import { create } from 'zustand';
import { FILTER_KEYS } from '../../../constants/constants';

// 기본 필터 상태 정의
const defaultFilters = {
    [FILTER_KEYS.GENDER]: '',
    [FILTER_KEYS.SUBCATEGORY_IDS]: [],
    [FILTER_KEYS.BRANDS]: [],
    [FILTER_KEYS.PRICE_MIN]: null,
    [FILTER_KEYS.PRICE_MAX]: null,
    [FILTER_KEYS.SIZE_VALUE_IDS]: [],
    [FILTER_KEYS.IS_SELLING]: false,
    [FILTER_KEYS.CONDITION]: '',
};

/**
 * 필터 스토어를 생성하는 함수.
 * 이 함수는 호출될 때마다 새로운 독립적인 스토어 인스턴스를 생성합니다.
 * @param {object} initialFilters - 스토어에 적용할 초기 필터 값
 * @returns {object} Zustand 스토어
 */
export const createFilterStore = (initialFilters = {}) => {
    const initialState = { ...defaultFilters, ...initialFilters };

    return create((set) => ({
        // 필터 상태
        filters: initialState,
        
        // 모바일 모달용 임시 필터 상태
        tempFilters: initialState,
        
        // 필터 변경 처리 함수
        handleFilterChange: (filterNameOrObject, value) => {
            set(state => {
                let newFilters;
                if (typeof filterNameOrObject === 'object' && filterNameOrObject !== null) {
                    newFilters = { ...filterNameOrObject };
                } else {
                    newFilters = { ...state.filters, [filterNameOrObject]: value };
                }

                if (newFilters[FILTER_KEYS.GENDER] === null) {
                    newFilters[FILTER_KEYS.GENDER] = '';
                }
                
                return { filters: newFilters };
            });
        },
        
        // 필터 초기화 함수
        initializeFilters: (initFilters) => {
            set(state => {
                const newFilters = { ...state.filters, ...initFilters };
                if (newFilters[FILTER_KEYS.GENDER] === null) {
                    newFilters[FILTER_KEYS.GENDER] = '';
                }

                // 값이 동일하면 상태 업데이트를 건너뛰어 불필요한 리렌더 방지
                const prev = state.filters;
                const isEqual = Object.keys(newFilters).every((key) => {
                    const a = prev[key];
                    const b = newFilters[key];
                    if (Array.isArray(a) && Array.isArray(b)) {
                        const setA = new Set(a);
                        return a.length === b.length && b.every((v) => setA.has(v));
                    }
                    return a === b;
                }) && Object.keys(prev).length === Object.keys(newFilters).length;

                if (isEqual) return state;

                return { filters: newFilters, tempFilters: newFilters };
            });
        },
        
        // 모든 필터 초기화
        resetAllFilters: () => {
            set({ filters: defaultFilters, tempFilters: defaultFilters });
        },

        // 개별 필터 업데이트
        updateFilter: (filterName, value) => {
            set(state => {
                const newFilters = { ...state.filters, [filterName]: value };
                return { filters: newFilters };
            });
        },

        // 모든 필터 클리어
        clearAllFilters: () => {
            set({ filters: defaultFilters, tempFilters: defaultFilters });
        },
        
        // ----- 모바일 모달(임시) 필터 관련 액션 ----- //
        handleTempFilterChange: (newTempFilters) => {
            set((state) => {
                const hasGender = Object.prototype.hasOwnProperty.call(
                    newTempFilters,
                    FILTER_KEYS.GENDER,
                );
                const merged = {
                    ...state.tempFilters,
                    ...newTempFilters,
                };
                // Only normalize/override gender when explicitly provided
                if (hasGender) {
                    merged[FILTER_KEYS.GENDER] =
                        newTempFilters[FILTER_KEYS.GENDER] == null
                            ? ''
                            : newTempFilters[FILTER_KEYS.GENDER];
                }
                return { tempFilters: merged };
            });
        },
        
        resetTempFilters: () => set(state => ({ tempFilters: { ...state.filters } })),

        clearTempFilters: () => set({ tempFilters: defaultFilters }),
        
        applyFilters: () => {
            set(state => {
                const applied = { ...state.tempFilters };
                if (applied[FILTER_KEYS.GENDER] === null) applied[FILTER_KEYS.GENDER] = '';
                
                return { filters: applied };
            });
        },
    }));
}

export function isFilterEmpty(filters) {
    return (
        (filters[FILTER_KEYS.GENDER] !== undefined ? filters[FILTER_KEYS.GENDER] : '') === defaultFilters[FILTER_KEYS.GENDER] &&
        Array.isArray(filters[FILTER_KEYS.SUBCATEGORY_IDS]) && filters[FILTER_KEYS.SUBCATEGORY_IDS].length === 0 &&
        Array.isArray(filters[FILTER_KEYS.BRANDS]) && filters[FILTER_KEYS.BRANDS].length === 0 &&
        (filters[FILTER_KEYS.PRICE_MIN] == null || filters[FILTER_KEYS.PRICE_MIN] === defaultFilters[FILTER_KEYS.PRICE_MIN]) &&
        (filters[FILTER_KEYS.PRICE_MAX] == null || filters[FILTER_KEYS.PRICE_MAX] === defaultFilters[FILTER_KEYS.PRICE_MAX]) &&
        Array.isArray(filters[FILTER_KEYS.SIZE_VALUE_IDS]) && filters[FILTER_KEYS.SIZE_VALUE_IDS].length === 0 &&
        filters[FILTER_KEYS.IS_SELLING] === defaultFilters[FILTER_KEYS.IS_SELLING] &&
        (filters[FILTER_KEYS.CONDITION] !== undefined ? filters[FILTER_KEYS.CONDITION] : '') === defaultFilters[FILTER_KEYS.CONDITION]
    );
}